import Modal from "@components/utils/Modal";

import { useTranslation } from "react-i18next";
import { Mixpanel } from "../../services/mixpanel";

import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import { RxCross1 } from "react-icons/rx";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { PRICING_FEATURES } from "../../constants";
import { useNavigate } from "react-router-dom";

const CheckIcon = () => {
  return (
    <div className="bg-blue-50 rounded-full p-1">
      <svg className="w-5 h-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    </div>
  );
};

const GenerateEmailAddress = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const { organization } = useSelector((state) => state.Auth);

  const hasEmailImportAccess = PRICING_FEATURES[organization?.plan]?.emailImport;

  return (
    <div>
      <Modal isOpen={open} onClose={() => setOpen(false)} innerClassName="flex max-w-[60rem] w-[90vw] max-h-[calc(100vh-5rem)]">
        <div className="w-full max-h-full flex flex-col overflow-y-hidden transform rounded-lg bg-white text-left align-middle shadow-xl transition-all">
          <div className="px-8 pt-8 pb-3 border-b flex justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">{hasEmailImportAccess ? t("generate_email_modal.title_ready") : t("generate_email_modal.title_upgrade")}</h2>
            <button type="submit" onClick={() => setOpen(false)}>
              <RxCross1 />
            </button>
          </div>
          <div className="p-8 overflow-auto">
            <GenerateEmailContainer hasEmailImportAccess={hasEmailImportAccess} onClose={() => setOpen(false)} organization={organization} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const GenerateEmailContainer = ({ hasEmailImportAccess, onClose, organization }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!hasEmailImportAccess) {
    return (
      <div className="flex flex-col space-y-6">
        <h3 className="text-xl font-semibold text-gray-800">{t("generate_email_modal.upgrade_title")}</h3>

        <div className="mt-4 mb-12 w-full h-fit flex flex-col items-center justify-center text-center">
          <div className="w-full bg-blue-100 rounded-lg px-4 py-2 text-left">
            <span className="text-gray-700 text-sm">
              {t("generate_email_modal.current_plan_label")} <b>{organization?.plan?.charAt(0).toUpperCase() + organization?.plan?.slice(1)}</b>
            </span>
          </div>
        </div>

        {/* Section Comment faire */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-800">{t("generate_email_modal.business_benefits_title")}</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckIcon />
              <span className="text-gray-700">{t("generate_email_modal.benefit_automated_reconciliation")}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckIcon />
              <span className="text-gray-700">{t("generate_email_modal.benefit_financial_dashboard")}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckIcon />
              <span className="text-gray-700">{t("generate_email_modal.benefit_custom_invoices")}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckIcon />
              <span className="text-gray-700">{t("generate_email_modal.benefit_automated_follow_up")}</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckIcon />
              <span className="text-gray-700">{t("generate_email_modal.benefit_fast_payment")}</span>
            </div>
          </div>
        </div>
        <div className="w-full flex justify-end space-y-4 pt-4">
          <button
            className="flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 transition-all duration-200 hover:scale-105"
            onClick={() => {
              Mixpanel.track(MIXPANEL_EVENTS.btn_upgrade_plan_email_address);
              navigate("/settings/my-subscription");
              setOpen(false);
            }}>
            <span className="mr-2">✨</span>
            {t("upgrade_your_plan")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {/* Section principale */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-800">{t("generate_email_modal.start_transferring_title")}</h3>
        <p className="text-gray-500 text-sm">{t("generate_email_modal.transfer_description")}</p>
      </div>

      <div className="flex items-center justify-between gap-2 w-full">
        <div className="w-full flex justify-between items-center border border-gray-300 rounded-lg pl-2 py-2">
          <input type="text" value={organization.expensesEmail} readOnly className="w-full bg-transparent focus:outline-none" />
        </div>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(organization.expensesEmail);
            toast.success(t("generate_email_modal.link_copied"));
          }}
          className="btn-primary px-6 flex items-center gap-2">
          {t("generate_email_modal.copy_link")}
        </button>
      </div>

      {/* Section Comment faire */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-800">{t("generate_email_modal.how_to_title")}</h4>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <CheckIcon />
            <span className="text-gray-700">{t("generate_email_modal.step_copy_email")}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckIcon />
            <span className="text-gray-700">{t("generate_email_modal.step_go_to_mailbox")}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckIcon />
            <span className="text-gray-700">{t("generate_email_modal.step_choose_email")}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckIcon />
            <span className="text-gray-700">{t("generate_email_modal.step_forward_email")}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckIcon />
            <span className="text-gray-700">{t("generate_email_modal.step_invoice_appears")}</span>
          </div>
        </div>
      </div>

      {/* Message final et bouton */}
      <div className="text-start space-y-4 pt-4">
        <p className="text-blue-500 font-medium">{t("generate_email_modal.ready_message")}</p>
      </div>
      <div className="w-full flex justify-end space-y-4 pt-4">
        <button onClick={onClose} className="btn-primary">
          {t("generate_email_modal.ready_button")}
        </button>
      </div>
    </div>
  );
};

export default GenerateEmailAddress;
